import { useEffect, useState } from 'react';
import { TAI_KHOAN_HOA_DON_DIEN_TU_QUERY_KEYS } from '@/features/he-thong/ket-noi-hddt/danh-muc-tai-khoan-hoa-don-dien-tu/constants/query_keys';
import {
  TaiKhoanHoaDonDienTu,
  TaiKhoanHoaDonDienTuInput,
  TaiKhoanHoaDonDienTuResponse
} from '@/types/schemas/tai-khoan-hoa-don-dien-tu.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseTaiKhoanHoaDonDienTuReturn {
  taiKhoanHoaDonDienTus: TaiKhoanHoaDonDienTu[];
  isLoading: boolean;
  selectedItem: TaiKhoanHoaDonDienTu | null;
  setSelectedItem: (item: TaiKhoanHoaDonDienTu | null) => void;
  addTaiKhoanHoaDonDienTu: (newTaiKhoanHoaDonDienTu: TaiKhoanHoaDonDienTuInput) => Promise<TaiKhoanHoaDonDienTu>;
  updateTaiKhoanHoaDonDienTu: (
    uuid: string,
    updatedTaiKhoanHoaDonDienTu: TaiKhoanHoaDonDienTuInput
  ) => Promise<TaiKhoanHoaDonDienTu>;
  deleteTaiKhoanHoaDonDienTu: (uuid: string) => Promise<void>;
  refreshTaiKhoanHoaDonDienTus: () => Promise<void>;
  getTaiKhoanHoaDonDienTuByCode: (code: string) => Promise<TaiKhoanHoaDonDienTu | null>;
}

/**
 * Hook for managing TaiKhoanHoaDonDienTu (Electronic Invoice Account) data
 *
 * This hook provides functions to fetch, create, update, and delete electronic invoice accounts.
 */
export const useTaiKhoanHoaDonDienTu = (
  initialTaiKhoanHoaDonDienTus: TaiKhoanHoaDonDienTu[] = []
): UseTaiKhoanHoaDonDienTuReturn => {
  const [taiKhoanHoaDonDienTus, setTaiKhoanHoaDonDienTus] =
    useState<TaiKhoanHoaDonDienTu[]>(initialTaiKhoanHoaDonDienTus);
  const [selectedItem, setSelectedItem] = useState<TaiKhoanHoaDonDienTu | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchTaiKhoanHoaDonDienTus = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<TaiKhoanHoaDonDienTuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_HOA_DON_DIEN_TU}/`
      );
      setTaiKhoanHoaDonDienTus(response.data.results);
    } catch (error) {
      console.error('Error fetching electronic invoice accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addTaiKhoanHoaDonDienTu = async (
    newTaiKhoanHoaDonDienTu: TaiKhoanHoaDonDienTuInput
  ): Promise<TaiKhoanHoaDonDienTu> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<TaiKhoanHoaDonDienTu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_HOA_DON_DIEN_TU}/`,
        newTaiKhoanHoaDonDienTu
      );

      // Update local state
      setTaiKhoanHoaDonDienTus(prev => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding electronic invoice account:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTaiKhoanHoaDonDienTu = async (
    uuid: string,
    updatedTaiKhoanHoaDonDienTu: TaiKhoanHoaDonDienTuInput
  ): Promise<TaiKhoanHoaDonDienTu> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.patch<TaiKhoanHoaDonDienTu>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_HOA_DON_DIEN_TU}/${uuid}/`,
        updatedTaiKhoanHoaDonDienTu
      );

      // Update local state
      setTaiKhoanHoaDonDienTus(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));

      return response.data;
    } catch (error) {
      console.error('Error updating electronic invoice account:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTaiKhoanHoaDonDienTu = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_HOA_DON_DIEN_TU}/${uuid}/`);

      // Update local state
      setTaiKhoanHoaDonDienTus(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting electronic invoice account:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getTaiKhoanHoaDonDienTuByCode = async (code: string): Promise<TaiKhoanHoaDonDienTu | null> => {
    if (!entity?.slug) return null;

    try {
      // First check if we already have it in local state
      const found = taiKhoanHoaDonDienTus.find(item => item.ma_tkhddt === code);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<TaiKhoanHoaDonDienTuResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_HOA_DON_DIEN_TU}/?ma_tkhddt=${code}`
      );

      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error) {
      console.error('Error fetching electronic invoice account by code:', error);
      return null;
    }
  };

  const refreshTaiKhoanHoaDonDienTus = async (): Promise<void> => {
    await fetchTaiKhoanHoaDonDienTus();
  };

  useEffect(() => {
    fetchTaiKhoanHoaDonDienTus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    taiKhoanHoaDonDienTus,
    isLoading,
    selectedItem,
    setSelectedItem,
    addTaiKhoanHoaDonDienTu,
    updateTaiKhoanHoaDonDienTu,
    deleteTaiKhoanHoaDonDienTu,
    refreshTaiKhoanHoaDonDienTus,
    getTaiKhoanHoaDonDienTuByCode
  };
};
